import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  notificationsProvider,
  type Notification,
} from "@/providers/notifications.provider";
import { useWebSocketReconnect } from "@/hooks/use-websocket-reconnect";

const WS_URL = `${process.env.BUN_PUBLIC_WS_BASE_URL}/v1/ws/connect`;

function isWsFrame(msg: unknown): msg is { action: string; payload: unknown } {
  return (
    typeof msg === "object" &&
    msg !== null &&
    "action" in msg &&
    "payload" in msg
  );
}

interface NotificationsContextValue {
  notifications: Notification[];
  unreadCount: number;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(
  null,
);

export function NotificationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const initialFetchDone = useRef(false);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const mergeNotifications = useCallback((incoming: Notification[]) => {
    setNotifications((prev) => {
      const map = new Map(prev.map((n) => [n.notification_id, n]));
      for (const n of incoming) {
        map.set(n.notification_id, n);
      }
      return Array.from(map.values()).sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    });
  }, []);

  const markRead = useCallback(async (id: string) => {
    await notificationsProvider.markRead(id);
    setNotifications((prev) =>
      prev.map((n) =>
        n.notification_id === id
          ? { ...n, is_read: true, read_at: new Date().toISOString() }
          : n,
      ),
    );
  }, []);

  const markAllRead = useCallback(async () => {
    await notificationsProvider.markRead("all");
    const now = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, is_read: true, read_at: now })),
    );
  }, []);

  useWebSocketReconnect(WS_URL, {
    onOpen() {
      initialFetchDone.current = false;
      notificationsProvider
        .list()
        .then((data) => {
          mergeNotifications(data);
          initialFetchDone.current = true;
        })
        .catch(() => {
          initialFetchDone.current = true;
        });
    },
    onMessage(evt) {
      try {
        const msg = JSON.parse(evt.data);
        if (isWsFrame(msg) && msg.action === "notification") {
          const n = msg.payload as Notification;
          mergeNotifications([n]);
          if (initialFetchDone.current) {
            toast(n.body, { description: n.action_type });
          }
        }
        if (isWsFrame(msg) && msg.action === "account_suspended") {
          // Server closes the socket right after this frame. Don't trust
          // the payload shape — just re-check the real enforcement status.
          window.dispatchEvent(new CustomEvent("auth:forbidden"));
        }
      } catch {}
    },
  });

  return (
    <NotificationsContext.Provider
      value={{ notifications, unreadCount, markRead, markAllRead }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      "useNotificationsContext must be used inside NotificationsProvider",
    );
  }
  return ctx;
}
